import { type FC, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';

import classes from './App.module.css';
import { AnimeMascot } from './components/AnimeMode/AnimeMascot';
import { AnimeOverlay } from './components/AnimeMode/AnimeOverlay';
import { SakuraPetals } from './components/AnimeMode/SakuraPetals';
import { DodoLogo } from './components/DodoLogo';
import { FigmaIcon, GitHubIcon, PluginIcon } from './components/LinkIcons/LinkIcons';
import { useLocale } from './i18n';
import { useAnimeMode } from './utils/AnimeModeContext';
import { isSoundEnabled, setAnimeSoundMode, setSoundEnabled } from './utils/playSound';
import { useKonamiCode } from './utils/useKonamiCode';

const DEACTIVATE_OVERLAY_DURATION_MS = 2500;
const ACTIVATE_OVERLAY_DURATION_MS = 1500;

const IconList = lazy(async () => {
	const m = await import('./components/IconList/IconList');

	return { default: m.IconList };
});

type Theme = 'dark' | 'light';

// Duration of the initial header animation in milliseconds
const HEADER_ANIMATION_DURATION_MS = 1500;

export const App: FC = () => {
	const { locale, setLocale, t } = useLocale();
	const headerRef = useRef<HTMLElement>(null);
	const [theme, setTheme] = useState<Theme>(() => {
		const saved = localStorage.getItem('theme') as Theme | null;

		return saved || 'dark';
	});
	const [soundEnabled, setSoundEnabledState] = useState(isSoundEnabled);

	// Anime mode
	const { isAnimeMode, toggleAnimeMode } = useAnimeMode();
	const [showOverlay, setShowOverlay] = useState(false);
	const [showDeactivate, setShowDeactivate] = useState(false);
	const overlayKeyRef = useRef(0);
	const deactivateKeyRef = useRef(0);

	const handleKonami = useCallback(() => {
		if (isAnimeMode) {
			// Deactivating
			deactivateKeyRef.current++;
			setShowDeactivate(true);
			setTimeout(() => setShowDeactivate(false), DEACTIVATE_OVERLAY_DURATION_MS);
		} else {
			// Activating
			overlayKeyRef.current++;
			setShowOverlay(true);
			setTimeout(() => setShowOverlay(false), ACTIVATE_OVERLAY_DURATION_MS);
		}
		toggleAnimeMode();
	}, [isAnimeMode, toggleAnimeMode]);

	useKonamiCode(handleKonami);

	// Sync anime mode to DOM and sound
	useEffect(() => {
		document.documentElement.setAttribute('data-anime', String(isAnimeMode));
		setAnimeSoundMode(isAnimeMode);
	}, [isAnimeMode]);

	useEffect(() => {
		document.documentElement.setAttribute('data-theme', theme);
		localStorage.setItem('theme', theme);
	}, [theme]);

	// Mark header as loaded after initial animation to prevent re-triggering
	useEffect(() => {
		const timer = setTimeout(() => {
			headerRef.current?.setAttribute('data-loaded', 'true');
		}, HEADER_ANIMATION_DURATION_MS);

		return () => clearTimeout(timer);
	}, []);

	const toggleTheme = (): void => {
		setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
	};

	const toggleSound = (): void => {
		const newValue = !soundEnabled;
		setSoundEnabledState(newValue);
		setSoundEnabled(newValue);
	};

	return (
		<div className={classes.root}>
			{/* Theatrical page reveal curtain */}
			<div className='page-curtain' aria-hidden='true' />
			<div className='reveal-horizon' aria-hidden='true' />

			{/* Anime mode overlays */}
			<AnimeOverlay key={`activate-${overlayKeyRef.current}`} show={showOverlay} deactivating={false} />
			<AnimeOverlay key={`deactivate-${deactivateKeyRef.current}`} show={false} deactivating={showDeactivate} />
			{isAnimeMode && <SakuraPetals />}
			{isAnimeMode && <AnimeMascot />}

			<header ref={headerRef} className={classes.header}>
				<div className={classes.controls}>
					<div className={classes.controlGroup}>
						<button
							type='button'
							className={classes.themeToggle}
							onClick={toggleTheme}
							aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
						>
							<span className={classes.toggleTrack} data-theme-state={theme}>
								<span className={classes.toggleThumb} />
							</span>
							<span className={`${classes.themeIcon} ${classes.moonIcon}`} data-active={theme === 'dark'}>
								<svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor'>
									<path d='M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z' />
								</svg>
							</span>
							<span className={`${classes.themeIcon} ${classes.sunIcon}`} data-active={theme === 'light'}>
								<svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor'>
									<path d='M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z' />
								</svg>
							</span>
						</button>
						<span className={classes.controlDivider} />
						<button
							type='button'
							className={classes.soundToggle}
							onClick={toggleSound}
							aria-label={soundEnabled ? t('disableSound') : t('enableSound')}
							data-enabled={soundEnabled}
						>
							{soundEnabled ? (
								<svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
									<path d='M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.584 5.106a.75.75 0 011.06 0c3.808 3.807 3.808 9.98 0 13.788a.75.75 0 01-1.06-1.06 8.25 8.25 0 000-11.668.75.75 0 010-1.06z' />
									<path d='M15.932 7.757a.75.75 0 011.061 0 6 6 0 010 8.486.75.75 0 01-1.06-1.061 4.5 4.5 0 000-6.364.75.75 0 010-1.06z' />
								</svg>
							) : (
								<svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
									<path d='M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 001.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 00-1.06-1.06l-1.72 1.72-1.72-1.72z' />
								</svg>
							)}
						</button>
						<span className={classes.controlDivider} />
						<div className={classes.langSwitcher} data-locale={locale}>
							<span className={classes.langThumb} />
							<button
								type='button'
								className={classes.langButton}
								onClick={() => setLocale('en')}
								data-active={locale === 'en'}
							>
								EN
							</button>
							<button
								type='button'
								className={classes.langButton}
								onClick={() => setLocale('ru')}
								data-active={locale === 'ru'}
							>
								RU
							</button>
						</div>
					</div>
				</div>

				<div className={classes.titleGroup}>
					<div className={classes.titleRow}>
						<DodoLogo size={64} />
						<h1 className={classes.title}>{t('title')}</h1>
					</div>
					<p className={classes.subtitle}>{t('subtitle')}</p>
				</div>

				<nav className={classes.links}>
					<a
						className={classes.link}
						target='_blank'
						href='https://www.figma.com/design/Gu5pXHklDKKE3mTemeN7pD/Icons?node-id=128-364&m=dev'
						rel='noreferrer'
					>
						<FigmaIcon size={16} />
						{t('figmaLink')}
					</a>
					<a
						className={classes.link}
						target='_blank'
						href='https://www.figma.com/community/plugin/1474136808233910835/figma-icons-to-github'
						rel='noreferrer'
					>
						<PluginIcon size={16} />
						{t('pluginLink')}
					</a>
					<a
						className={classes.link}
						target='_blank'
						href='https://github.com/dodopizza/icons'
						rel='noreferrer'
					>
						<GitHubIcon size={16} />
						{t('githubLink')}
					</a>
				</nav>
			</header>

			<main className={classes.main}>
				<Suspense>
					<IconList />
				</Suspense>
			</main>
		</div>
	);
};
